import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCircleInfo } from "react-icons/fa6";
import { Tooltip } from "react-tooltip";
import { Alert } from "reactstrap";

import MemberSearch from "@components/Booking/AddGuest/MemberSearch/MemberSearch";
import SimpleModal from "@components/shared/SimpleModal";
import { useUserLogin } from "@hooks/useUserLogin/useUserLogin";
import { BookingFetchResponseType } from "@models/services/booking";

import "@styles/kiosk.scss";

const UserLogin = ({ slotInfo }: { slotInfo: BookingFetchResponseType }) => {
  const { t } = useTranslation();

  const {
    actions,
    clearAddGuestError,
    data,
    handleGuestChange,
    handleLogin,
    handlePinChange,
    handlePinKeyDown,
    handlePinPaste,
    isLoading,

    isPinChecking,
    isPinComplete,
    loginError,
    pin,
    pinCheckError,
    pinInputRefs,
    selectedGuest,
    setIsModalOpen,
    setters,
    showLoginModal,
  } = useUserLogin({ slotInfo });

  const [isForgotPinModalOpen, setIsForgotPinModalOpen] = useState(false);

  const isDisabled =
    isLoading ||
    !isPinComplete ||
    !selectedGuest ||
    isPinChecking ||
    !!pinCheckError;

  useEffect(() => {
    return () => {
      setIsModalOpen(false);
    };
  }, []);

  return (
    <div
      id="fg-booking-user-login-section"
      data-testid="kiosk-user-login-section"
    >
      <SimpleModal
        isOpen={showLoginModal}
        onClose={() => setIsModalOpen(false)}
        backdrop="static"
        title={t("kiosk.userLogin.modalTitle")}
        size="lg"
        centered={true}
      >
        <form onSubmit={handleLogin} className="p-4">
          <div className="mb-4">
            <MemberSearch
              noOptionsMessage={t("addGuest.noMembers")}
              selectedGuest={selectedGuest}
              loadOptions={actions.loadOptions}
              onChangeGuest={handleGuestChange}
              clearError={clearAddGuestError}
              placeholder={t("addGuest.searchMembers")}
              filterByClubMembers={true}
              setFilteredClub={setters.setFilteredClub}
              filteredClub={data.filteredClub}
              clubFilterList={data.clubFilterList}
              memberTitle={
                <label
                  htmlFor="pin-input-0"
                  className="form-label fs-5 fw-semibold"
                >
                  {t("kiosk.userLogin.selectNameLabel")}
                </label>
              }
            />

            {isPinChecking && (
              <div className="d-flex align-items-center gap-2 text-muted small mt-1">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                {t("kiosk.userLogin.checkingPin")}
              </div>
            )}

            {!isPinChecking && pinCheckError && (
              <Alert color="danger" className="gap-2 mt-2 mb-0 py-2 px-3">
                <span>{pinCheckError}</span>
              </Alert>
            )}
          </div>

          {selectedGuest && !isPinChecking && (
            <div className="mb-4">
              <label
                htmlFor="pin-input-0"
                className="form-label fs-5 fw-semibold"
              >
                {t("kiosk.userLogin.enterPinLabel")}

                <small
                  className="fg-booking-min-max-badge ms-2"
                  data-tooltip-id="fg-booking-enter-pin-tooltip"
                >
                  <FaCircleInfo />
                </small>

                <Tooltip
                  id="fg-booking-enter-pin-tooltip"
                  className="fg-booking-enter-pin-tooltip"
                  place="top"
                  clickable={true}
                >
                  {t("kiosk.userLogin.enterPinTooltip")}
                </Tooltip>
              </label>
              <div className="pin-input-container">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    id={`pin-input-${index}`}
                    ref={el => {
                      pinInputRefs.current[index] = el;
                    }}
                    type="password"
                    inputMode="text"
                    maxLength={1}
                    value={digit}
                    onChange={e => handlePinChange(index, e.target.value)}
                    onKeyDown={e => handlePinKeyDown(index, e)}
                    onPaste={index === 0 ? handlePinPaste : undefined}
                    className="form-control form-control-lg pin-input-field"
                    disabled={isLoading}
                    required
                  />
                ))}
              </div>
              <div className="d-flex align-items-center justify-content-between mt-2">
                <small className="text-muted">
                  {t("kiosk.userLogin.pinHint")}
                </small>
                <button
                  type="button"
                  id="forgot-pin-btn"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => setIsForgotPinModalOpen(true)}
                >
                  {t("kiosk.userLogin.forgotPin")}
                </button>
              </div>

              <SimpleModal
                isOpen={isForgotPinModalOpen}
                onClose={() => setIsForgotPinModalOpen(false)}
                title={t("kiosk.userLogin.forgotPin")}
                size="sm"
                centered={true}
                zIndex={1060}
                footerContent={
                  <button
                    type="button"
                    className="btn flex-fill fg-btn btn-block fg-btn-filled-darkblue"
                    onClick={() => setIsForgotPinModalOpen(false)}
                  >
                    {t("ok")}
                  </button>
                }
              >
                <p className="mb-0">{t("kiosk.userLogin.forgotPinTooltip")}</p>
              </SimpleModal>

              {loginError && (
                <Alert color="danger" className="gap-2 mt-2 mb-0 py-2 px-3">
                  <span>{loginError}</span>
                </Alert>
              )}
            </div>
          )}

          <div className="d-grid gap-2">
            <button
              type="submit"
              className="btn fg-btn mb-1 fg-btn-filled-darkblue btn-lg"
              disabled={isDisabled}
            >
              {isLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                    data-testid="logging-in-spinner"
                  ></span>
                  {t("kiosk.userLogin.loggingIn")}
                </>
              ) : (
                t("kiosk.userLogin.continue")
              )}
            </button>

            <button
              type="button"
              className="btn btn-outline-secondary btn-lg"
              onClick={() => setIsModalOpen(false)}
              disabled={isLoading}
            >
              {t("kiosk.userLogin.cancel")}
            </button>
          </div>
        </form>
      </SimpleModal>
    </div>
  );
};

export default memo(UserLogin);
